import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Components, Feedback, Utils, translate,
} from 'versafleet-core';
import * as Sentry from '@sentry/browser';
import TaskModule from 'modules/task';
import ReduxActions from 'reduxActions';
import addOnsSettings from 'modules/addOns/addOnsConstants';
import featuresSettings from 'pages/featuresConstants';
import withRouterHooks from 'utils/routerHelper';
import { isUserReadOnly } from 'utils/accessHelper';

const {
  FullScreenLoading, Formatter, Helper, TranslationHelper, PusherHelper,
} = Utils;

const SORT_OPTIONS = [{
  value: 'id',
  text: 'Created At',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'time_from',
  text: 'Task Time window',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'jobs.time_from',
  text: 'Job Time window',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'updated_at',
  text: 'Updated At',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'billing_accounts.name',
  text: 'Account name',
  ascendingText: 'A to Z',
  descendingText: 'Z to A',
}, {
  value: 'addresses.line_1',
  text: 'Address',
  ascendingText: 'A to Z',
  descendingText: 'Z to A',
}, {
  value: 'addresses.zip',
  text: 'Postal Code',
  ascendingText: '0 to 9',
  descendingText: '9 to 0',
}];

class SubmissionsTaskPageContent extends React.Component {
  constructor(props) {
    super(props);
    this.dataId = null;
    this.state = {
      checkedStatus: {},
      filter: this._getDefaultFilter(),
      disableButton: { print: false, printTaskLabel: false },
      showCancelModal: false,
      showFilter: false,
    };
  }

  UNSAFE_componentWillMount() {
    this.props.dispatch(ReduxActions.constant.fetchList());
    this.props.dispatch(ReduxActions.account.fetch());
    this.props.dispatch(ReduxActions.account.fetchTimeWindows());
    this.props.dispatch(ReduxActions.customLayout.fetch());
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.meta !== this.props.meta) {
      this._updateCheckedStatus({});
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.filter !== this.state.filter) {
      this._loadList();
    }
  }

  _toggleShowFilter = () => {
    this.setState(prevState => ({
      showFilter: !prevState.showFilter,
    }));
  }

  _resetFilters() {
    this.setState({ filter: this._getDefaultFilter() }, () => {
      this._loadList();
    });
  }

  _getDefaultFilter() {
    const date = this.props.filter.date;
    return {
      date: Array.isArray(date) ? date : [date, date],
      keyword: [],
      state: this._getTaskStatusOptions().map(option => option.value),
      job_state: this._getJobStatusOptions().map(option => option.value),
      sort_by: SORT_OPTIONS[0].value,
      order_by: 'desc',
      page: 1,
      per_page: 20,
      time_window_id: [],
      time_from: null,
      time_to: null,
      job_time_window_id: [],
      job_time_from: null,
      job_time_to: null,
      tags: [],
      billing_account_id: [],
      task_contact_person: [],
      pickup_address: this.props.filter.pickup_address || [],
      delivery_address: this.props.filter.delivery_address || [],
      job_type: Helper.isNullOrUndefined(this.props.filter.job_type)
        ? this._getJobTypeOptions().map(option => option.value) : this.props.filter.job_type,
      custom_field_description_id: null,
      custom_field_value: null,
    };
  }

  _getStatusFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const taskStatusOptions = this._getTaskStatusOptions();
    const taskStatusCount = filter.state.length > 0
      && filter.state.length <= taskStatusOptions.length ? filter.state.length : null;

    let text = '';
    if (taskStatusCount === 1) {
      text = Formatter.capitalizeFirstLetter(strings.Task.status[filter.state[0]]);
    } else if (taskStatusCount === taskStatusOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.status.itemPlural}`;
    } else if (taskStatusCount === 0 || taskStatusCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.status.item}`;
    } else {
      text = `${taskStatusCount} ${strings.Filter.buttons.status.itemPlural}...`;
    }

    return text;
  }

  _getTypeFilterText() {
    const { strings } = this.props;
    const { filter } = this.state;
    const jobTypeOptions = this._getJobTypeOptions();
    const taskTypeCount = filter.job_type.length > 0
      && filter.job_type.length <= jobTypeOptions.length ? filter.job_type.length : null;

    let text = '';
    if (taskTypeCount === 1) {
      text = strings.Filter.buttons.taskType.type[filter.job_type[0]];
    } else if (taskTypeCount === jobTypeOptions.length) {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.taskType.itemPlural}`;
    } else if (taskTypeCount === 0 || taskTypeCount === null) {
      text = `${strings.Filter.buttons.no} ${strings.Filter.buttons.taskType.item}`;
    } else {
      text = `${taskTypeCount} ${strings.Filter.buttons.taskType.itemPlural}...`;
    }

    return text;
  }

  _getTagFilterText() {
    const { taskTags, strings } = this.props;
    const value = this.state.filter.tags;
    const options = taskTags.map(option => ({ value: option, text: option }));

    let text = '';
    if (value.length <= 1) {
      text = strings.Filter.buttons.tag.task.item;
    } else if (value.length > 1 && value.length < options.length) {
      text = strings.Filter.buttons.tag.multiple;
    } else {
      text = `${strings.Filter.buttons.all} ${strings.Filter.buttons.tag.task.itemPlural}`;
    }

    return text;
  }

  _getSubTextTaskTimeRange() {
    // eslint-disable-next-line camelcase
    const { time_from, time_to } = this.state.filter;

    // eslint-disable-next-line camelcase
    return `${Helper.getTimeFromSecond(time_from * 60)} - ${Helper.getTimeFromSecond(time_to * 60)}`;
  }

  _getSubTextJobTimeRange() {
    // eslint-disable-next-line camelcase
    const { job_time_from, job_time_to } = this.state.filter;

    // eslint-disable-next-line camelcase
    return `${Helper.getTimeFromSecond(job_time_from * 60)} - ${Helper.getTimeFromSecond(job_time_to * 60)}`;
  }

  _getModelOptions() {
    const strings = this.props.strings.Job.constants;
    return [
      { value: 'task', text: Formatter.capitalizeFirstLetter(strings.taskPlural) },
      { value: 'job', text: Formatter.capitalizeFirstLetter(strings.jobPlural) },
    ];
  }

  _getTaskStatusOptions() {
    const strings = this.props.strings.Task.status;
    return this.props.statusVisibility === 'basic' ? [{
      text: strings.cancelled,
      value: 'cancelled',
    }, {
      text: strings.pending,
      value: 'pending',
    }] : [{
      text: strings.unassigned,
      value: 'unassigned',
    }, {
      text: strings.assigned,
      value: 'assigned',
    }, {
      text: strings.waiting_for_acknowledgement,
      value: 'waiting_for_acknowledgement',
    }, {
      text: strings.acknowledged,
      value: 'acknowledged',
    }, {
      text: strings.started,
      value: 'started',
    }, {
      text: strings.arrived,
      value: 'arrived',
    }, {
      text: strings.successful,
      value: 'successful',
    }, {
      text: strings.failed,
      value: 'failed',
    }, {
      text: strings.declined,
      value: 'declined',
    }, {
      text: strings.cancelled,
      value: 'cancelled',
    }, {
      text: strings.collected,
      value: 'collected',
    }];
  }

  _getJobTypeOptions() {
    const strings = this.props.strings.Job.constants;
    return [{
      text: Formatter.capitalizeFirstLetter(strings.pickup),
      value: 'pickup',
    }, {
      text: Formatter.capitalizeFirstLetter(strings.delivery),
      value: 'delivery',
    }, {
      text: strings.pointToPoint,
      value: 'p2p',
    }];
  }

  _getJobStatusOptions() {
    const strings = this.props.strings.Job.status;
    return [{
      text: strings.draft,
      value: 'draft',
    }, {
      text: strings.rejected,
      value: 'rejected',
    }, {
      text: strings.submitted,
      value: 'submitted',
    }, {
      text: strings.cancelled,
      value: 'cancelled',
    }];
  }

  _getSelectedIds() {
    return Object.keys(this.state.checkedStatus)
      .filter(key => this.state.checkedStatus[key])
      .map(key => parseInt(key, 10));
  }

  _onFilterChange = (fieldName, value) => {
    this.setState((prevState) => {
      const filter = Helper.setFilter(prevState.filter, fieldName, value);

      // Each time a filter is changed, check if any of the keys exist in
      // the filter store. If so, update the filter store to the filter changed
      const myJobsFilter = this.props.filter;
      Object.keys(myJobsFilter).forEach((key) => {
        if (typeof filter[key] !== 'undefined') {
          myJobsFilter[key] = filter[key];
        }
      });
      this.props.dispatch(ReduxActions.filter.updateMyJobsTaskFilter(myJobsFilter));

      return {
        filter,
      };
    });
  }

  _onModelChange = (value) => {
    this.props.onModelChange(value);
  }

  _loadList() {
    FullScreenLoading.FullScreenLoadingManager.open(this.props.strings.Task.page.loadingItems);
    this.props.dispatch(ReduxActions.task.fetchList(this.state.filter))
      .then(() => {
        FullScreenLoading.FullScreenLoadingManager.close();
      });
  }

  _checkItem = (id) => {
    this.setState(prevState => ({
      checkedStatus: {
        ...prevState.checkedStatus,
        [id]: !prevState.checkedStatus[id],
      },
    }));
  }

  _updateCheckedStatus = (checkedStatus) => {
    this.setState({ checkedStatus });
  }

  _viewNewJobForm = () => {
    this.props.navigate('/submissions/jobs/new');
  }

  _viewImportJobForm = () => {
    this.props.navigate('/submissions/jobs/import');
  }

  _viewExportJobForm = () => {
    this.props.navigate('/submissions/jobs/export');
  }

  _printMultiple = () => {
    this._printTasks(false);
    this._disableButton('print');
  }

  _printTaskLabelMultiple = () => {
    this._printTasks(true);
    this._disableButton('printTaskLabel');
  }

  _printTasks(isLabel) {
    const pusherId = `${isLabel ? 'task_label' : 'task'}_print_${PusherHelper.getNewPusherId()}`;
    const query = { ids: this._getSelectedIds(), pusher_id: pusherId };
    const printTasks = () => {
      PusherHelper.bindChannel('user', pusherId,
        result => this._handlePrintResult(result, pusherId));
      if (isLabel) {
        ReduxActions.task.printTaskLabelMultiple(query);
      } else {
        ReduxActions.task.printMultiple(query);
      }
    };
    Feedback.Progress.Manager.updateProgress(pusherId, { onRetry: printTasks });
    printTasks();
  }

  _handlePrintResult = (result, pusherId) => {
    Feedback.Progress.Manager.updateProgress(pusherId, result);
    if (result.status === 'failed') {
      PusherHelper.unbindChannel('user', pusherId);
      Sentry.captureException(result.error);
    }
    if (result.status === 'completed') {
      window.open(result.url, '_blank');
      PusherHelper.unbindChannel('user', pusherId);
    }
  }

  _tagMultiple = (ids, tagList) => {
    this.props.dispatch(ReduxActions.task.tagMultiple(ids, tagList));
  }

  _disableButton(buttonName) {
    this.setState(prevState => ({
      disableButton: { ...prevState.disableButton, [buttonName]: true },
    }), async () => {
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.setState(prevState => ({
        disableButton: { ...prevState.disableButton, [buttonName]: false },
      }));
    });
  }

  _showCancelModal(dataId) {
    this.setState({ showCancelModal: true });
    this.dataId = dataId;
  }

  _hideCancelModal() {
    this.setState({ showCancelModal: false });
    this.dataId = null;
  }

  async _confirmCancel() {
    try {
      await this.props.dispatch(ReduxActions.task.cancel(this.dataId));
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <TaskModule.CancelSuccessMessage count={1} />,
      );
    } catch (error) {
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <div className="vf-task__cancel-dialog__error-message">
          {Utils.TranslationHelper.replaceTag(this.props.strings.Task.cancelDialog.errorMessage, {
            count: 1,
          })}
        </div>,
      );
    }

    this.setState({ showCancelModal: false });
  }

  _renderFilterPanel = () => (
    <Components.ActionPanel.FilterPanel
      rightAnchoredButtons={[
        <Components.ActionPanel.FilterResetButton
          onClick={() => this._resetFilters()}
        />,
      ]}
      showFilter={this.state.showFilter}
    >
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getStatusFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('state', value)}
          options={this._getTaskStatusOptions()}
          value={this.state.filter.state}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={this.state.filter.tags.length === 1 ? this.state.filter.tags[0] : undefined}
        text={this._getTagFilterText()}
      >
        <Components.ActionPanel.FilterTagDropdown
          onChange={value => this._onFilterChange('tags', value)}
          options={this.props.taskTags.map(option => ({ value: option, text: option }))}
          placeholder={this.props.strings.Filter.buttons.tag.placeholder}
          value={this.state.filter.tags}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={(!Helper.isNullOrUndefined(this.state.filter.time_from)
          && !Helper.isNullOrUndefined(this.state.filter.time_to))
          ? this._getSubTextTaskTimeRange() : undefined}
        text={this.props.strings.Filter.buttons.timeRange.taskTimeRange}
      >
        <Components.ActionPanel.FilterTimeCheckbox
          onChange={(fieldName, v) => this._onFilterChange(fieldName, v)}
          options={this.props.timeWindows}
          values={this.state.filter}
        />
      </Components.ActionPanel.DropdownButton>
      <Components.ActionPanel.DropdownButton
        showArrow
        subText={(!Helper.isNullOrUndefined(this.state.filter.job_time_from)
          && !Helper.isNullOrUndefined(this.state.filter.job_time_to))
          ? this._getSubTextJobTimeRange() : undefined}
        text={this.props.strings.Filter.buttons.timeRange.jobTimeRange}
      >
        <Components.ActionPanel.FilterTimeCheckbox
          onChange={(fieldName, v) => this._onFilterChange(`job_${fieldName}`, v)}
          options={this.props.timeWindows}
          values={{
            time_window_id: this.state.filter.job_time_window_id,
            time_from: this.state.filter.job_time_from,
            time_to: this.state.filter.job_time_to,
          }}
        />
      </Components.ActionPanel.DropdownButton>
      <TaskModule.FilterAccountButton
        id="vf-task__action-panel__filter-billing-account"
        onChange={value => this._onFilterChange('billing_account_id', value)}
        value={this.state.filter.billing_account_id}
      />
      <TaskModule.FilterTaskContactPersonButton
        date={this.state.filter.date}
        id="vf-task__action-panel__filter-task-contact-person"
        onChange={value => this._onFilterChange('task_contact_person', value)}
        value={this.state.filter.task_contact_person}
      />
      <TaskModule.FilterAddressButton
        id="vf-task__action-panel__filter-pickup-address"
        onChange={value => this._onFilterChange('pickup_address', value)}
        value={this.state.filter.pickup_address}
      />
      <TaskModule.FilterAddressButton
        id="vf-task__action-panel__filter-delivery-address"
        isDeliveryFilter
        onChange={value => this._onFilterChange('delivery_address', value)}
        value={this.state.filter.delivery_address}
      />
      <Components.ActionPanel.DropdownButton
        showArrow
        text={this._getTypeFilterText()}
      >
        <Components.Input.CheckboxGroup.WithArray
          onChange={value => this._onFilterChange('job_type', value)}
          options={this._getJobTypeOptions()}
          value={this.state.filter.job_type}
        />
      </Components.ActionPanel.DropdownButton>
      {this._renderFilterCustomFieldButton()}
    </Components.ActionPanel.FilterPanel>
  );

  _renderFilterCustomFieldButton() {
    const { account } = this.props;
    const { filter } = this.state;

    if (!Utils.Helper.isNullOrUndefined(account.addons_setting)
      && account.addons_setting.custom_field === addOnsSettings.subscribed
      && account.features_setting.custom_field_filter === featuresSettings.enabled) {
      return (
        <TaskModule.FilterCustomFieldButton
          childOnChange={(value) => {
            this._onFilterChange('custom_field_value', value);
          }}
          childValue={filter.custom_field_value}
          date={filter.date}
          id="vf-task__action-panel__custom-field"
          parentOnChange={(value) => {
            this._onFilterChange('custom_field_description_id', value);
          }}
          parentValue={filter.custom_field_description_id}
        />
      );
    }

    return false;
  }

  _renderMultiSelectPanel = () => (
    <Components.ActionPanel.MultiSelectPanel
      checkedStatus={this.state.checkedStatus}
      dataIds={this.props.tasks.map(task => task.id)}
      updateCheckedStatus={this._updateCheckedStatus}
    >
      <span className="vf-task__page__multi-select-panel__count">
        {TranslationHelper.replaceTag(this.props.strings.Task.page.multiSelectCount, {
          count: (
            <span className="vf-task__page__multi-select-panel__count--number">
              {Object.keys(this.state.checkedStatus)
                .filter(key => this.state.checkedStatus[key]).length}
            </span>
          ),
        })}
      </span>

      {!isUserReadOnly() ? (
        <TaskModule.BulkTagButton
          hint="Tag"
          iconClassName="versa-add"
          id="vf-submissions__tasks__bulk-tag-button"
          isAddingTag={this.props.isAddingTag}
          tagMultiple={this._tagMultiple}
          tags={this.props.tags}
          taskIds={this._getSelectedIds()}
          textIconClassName="versa-tags"
        />
      ) : false}

      {!isUserReadOnly() ? (
        <TaskModule.BulkArchiveButton
          dataIds={this._getSelectedIds()}
          id="vf-submissions__tasks__bulk-archive-button"
        />
      ) : false}

      <Components.Button
        disabled={this.state.disableButton.print}
        hint={this.props.strings.Task.page.buttons.print}
        iconClassName="versa-print"
        id="vf-submissions__tasks__bulk-print-button"
        onClick={this._printMultiple}
      />

      {!Helper.isNullOrUndefined(this.props.account.addons_setting)
        && this.props.account.addons_setting.custom_task_label === addOnsSettings.subscribed
        ? (
          <Components.Button
            disabled={this.state.disableButton.printTaskLabel}
            hint={this.props.strings.Task.page.buttons.label}
            iconClassName="versa-label"
            id="vf-task__list-item__task-label__button"
            onClick={this._printTaskLabelMultiple}
          />
        ) : false}
    </Components.ActionPanel.MultiSelectPanel>
  )

  _renderActionPanel() {
    const { meta, strings } = this.props;
    const { filter } = this.state;

    const sortOption = SORT_OPTIONS.find(option => option.value === filter.sort_by);
    const sortButtonText = Formatter.capitalizeEachWord(sortOption[`${filter.order_by}endingText`]);

    return (
      <Components.ActionPanel
        renderFilterPanel={this._renderFilterPanel}
        renderMultiSelectPanel={this._renderMultiSelectPanel}
      >
        <Components.Input.ButtonSelect
          id="vf-submissions__tasks__model-toggle"
          onChange={this._onModelChange}
          options={this._getModelOptions()}
          value="task"
        />
        <Components.Paginator
          currentPage={meta.page}
          id="vf-submissions__tasks__paginator"
          isCountCustomizable
          itemsPerPage={meta.per_page}
          model="tasks"
          onChange={this._onFilterChange}
          totalItems={meta.total}
        />
        <Components.ActionPanel.DropdownButton
          showArrow
          text={Helper.getRangeDateString(this.state.filter.date[0], this.state.filter.date[1],
            this.props.account.language)}
        >
          <Components.ActionPanel.FilterDateRangePicker
            locale={this.props.account.language}
            onChange={value => this._onFilterChange('date', value)}
            values={this.state.filter.date}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.FilterTagSearch
          id="vf-submissions__tasks__search-input"
          onChange={value => this._onFilterChange('keyword', value)}
        />
        <Components.ActionPanel.FilterShowButton
          onClick={() => { this._toggleShowFilter(); }}
          showFilter={this.state.showFilter}
        />
        <Components.ActionPanel.DropdownButton
          iconClassName="versa-sort"
          id="vf-submissions__tasks__filter-tuner"
          text={sortButtonText}
        >
          <Components.Sorter
            id="vf-submissions__tasks__filter-tuner__sort"
            onChange={value => this._onFilterChange(null, value)}
            options={SORT_OPTIONS}
            orderValue={this.state.filter.order_by}
            value={this.state.filter.sort_by}
          />
        </Components.ActionPanel.DropdownButton>
        <Components.ActionPanel.ButtonGroup>
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.add}
              iconClassName="versa-add"
              id="vf-submissions__tasks__new-job-button"
              onClick={this._viewNewJobForm}
            />
          ) : false}
          {!isUserReadOnly() ? (
            <Components.Button
              hint={strings.Job.page.buttons.import}
              iconClassName="versa-upload"
              id="vf-submissions__tasks__import-button"
              onClick={this._viewImportJobForm}
            />
          ) : false}
          <Components.Button
            hint={strings.Job.page.buttons.export}
            iconClassName="versa-download"
            id="vf-submissions__tasks__export-button"
            onClick={this._viewExportJobForm}
          />
        </Components.ActionPanel.ButtonGroup>
      </Components.ActionPanel>
    );
  }

  _renderPageContent() {
    return this.props.tasks.map(task => (
      <TaskModule.ListItem
        account={this.props.account}
        checkItem={this._checkItem}
        checkedStatus={this.state.checkedStatus[task.id]}
        customTaskLayout={this.props.customTaskLayout}
        data={task}
        dispatch={this.props.dispatch}
        id="vf-submissions__tasks__list-item"
        key={task.id}
        showCancelModal={dataId => this._showCancelModal(dataId)}
        tags={this.props.systemTags}
      />
    ));
  }

  _renderCancelModal() {
    const strings = this.props.strings.Task.cancelDialog;

    return (
      <Components.Modal.Warning
        headerText={strings.header}
        leftButtonOnClick={() => this._confirmCancel()}
        leftButtonText={strings.confirmButton}
        rightButtonOnClick={() => this._hideCancelModal()}
        rightButtonText={strings.cancelButton}
        visible={this.state.showCancelModal}
      >
        {strings.line1}
      </Components.Modal.Warning>
    );
  }

  render() {
    return (
      <div>
        {this._renderActionPanel()}
        {this._renderPageContent()}
        {this._renderCancelModal()}
        {this.props.children}
      </div>
    );
  }
}

SubmissionsTaskPageContent.displayName = 'Submissions Task Page';

SubmissionsTaskPageContent.propTypes = {
  account: PropTypes.shape(),
  children: PropTypes.node,
  customTaskLayout: PropTypes.shape(),
  dispatch: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    job_type: PropTypes.string,
    pickup_address: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    delivery_address: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  }).isRequired,
  isAddingTag: PropTypes.bool.isRequired,
  meta: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  onModelChange: PropTypes.func.isRequired,
  statusVisibility: PropTypes.string,
  strings: PropTypes.shape().isRequired,
  systemTags: PropTypes.shape({}),
  tags: PropTypes.arrayOf(PropTypes.string),
  taskTags: PropTypes.arrayOf(PropTypes.string),
  tasks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
  })).isRequired,
  timeWindows: PropTypes.arrayOf(PropTypes.shape({
    archived: PropTypes.bool,
    id: PropTypes.number,
    name: PropTypes.string,
    time_from: PropTypes.number,
    time_to: PropTypes.number,
  })),
};

SubmissionsTaskPageContent.defaultProps = {
  account: null,
  children: null,
  customTaskLayout: {
    col_2: ['account_name', 'task_contact_person', 'task_id', 'tracking_id'],
    col_3: ['task_address', 'task_time_range'],
    col_4: ['task_scheduled_time'],
    col_5: ['task_tags', 'item_summary'],
  },
  tags: [],
  statusVisibility: 'basic',
  taskTags: [],
  timeWindows: [{}],
  systemTags: {},
};

function mapStateToProps(state) {
  const {
    account, constant, customLayout, filter, task, profile,
  } = state;

  return {
    account: account.account,
    customTaskLayout: customLayout.customLayout.custom_myjobs_task_layout,
    tags: constant.constant.jobs.tags,
    meta: task.taskMeta,
    tasks: task.tasksIds.map(id => task.tasks[id]),
    isAddingTag: task.isAddingTag,
    filter: filter.filter.myJobs,
    // profile.profile is sometimes null after login, causing a blank page.
    // Unsure why this happens, but this 'fix' works and the value of status_visibility
    // eventually corrects itself to the actual value after login.
    statusVisibility: profile.profile ? profile.profile.myjobs_setting.status_visibility : 'basic',
    systemTags: task.systemTags,
    taskTags: constant.constant.tasks.tags,
    timeWindows: account.timeWindows,
  };
}

export default withRouterHooks(connect(mapStateToProps)(translate('')(SubmissionsTaskPageContent)));
